import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 2.4 visual preview at /preview/lesson/2.4
 *
 * "The AI as a teammate, not a replacement" — Week 2, lesson 4.
 *
 * The character lesson. 2.1 was tool. 2.2 was prompt. 2.3 was answer.
 * 2.4 is you and how you actually work with AI day to day. Teaches the
 * four teammate modes (Sparring partner, Drafter, Tutor, Editor) and the
 * three traps (outsourcing taste, voice, and judgment).
 *
 * Promotes "Cognitive offloading" as the new vocab term with the full
 * 4-block treatment. 3 posture drills where students pick which mode
 * fits a given task, or walk away.
 */

export default function LessonPreviewPage2_4() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const ids = [
      'sec-cold-open',
      'sec-from-last-time',
      'sec-teaching',
      'sec-your-turn',
      'sec-quick-check',
      'sec-lock-card',
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = ids.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------- Header ----------

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 2 · Working with AI · Lesson 2.4
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The AI as a teammate, not a replacement
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="h-3 w-px bg-line" />
        <span>Threads: Character · Judgment</span>
        <span className="h-3 w-px bg-line" />
        <span className="text-amber">3 posture drills</span>
      </div>
    </header>
  );
}

// ---------- Cold open ----------

function ColdOpen() {
  return (
    <Section
      id="sec-cold-open"
      label="Cold open"
      eyebrow="Two students. Same essay. Same grade. Six months apart."
      accent="amber"
    >
      <p>
        Same English class. Same essay assignment. Same deadline. Both students
        get a B+. On paper they did the same thing. Watch what actually
        happened.
      </p>

      <StudentSplit />

      <p className="mt-4">
        Same grade. Same week. Different person at the end of it. Student A got
        an essay. Student B got an essay and got sharper. The grade does not
        show the difference. Six months later the difference is the only thing
        that shows.
      </p>
      <p className="text-text">
        2.1 was the tool. 2.2 was the prompt. 2.3 was the answer. 2.4 is you.
        How you sit at the table with AI is the whole game.
      </p>
    </Section>
  );
}

// ---------- From last time ----------

function FromLastTime() {
  return (
    <Section
      id="sec-from-last-time"
      label="From last time · Lesson 2.3"
      eyebrow="Three questions on the trust ladder. No looking back."
      accent="blue"
    >
      <ol className="space-y-3 text-text">
        <li>
          <span className="font-mono text-amber">01.</span> Name the three
          zones on the trust ladder.
        </li>
        <li>
          <span className="font-mono text-amber">02.</span> What is
          hallucination, in one sentence?
        </li>
        <li>
          <span className="font-mono text-amber">03.</span> Name two of the
          three walk-away signals.
        </li>
      </ol>
      <p className="mt-5 text-sm text-muted">
        If any of those felt fuzzy, go back to 2.3 before you start this
        lesson. The trust ladder decides what to do with an answer. This lesson
        decides who you are when you ask.
      </p>
    </Section>
  );
}

// ---------- Teaching ----------

function Teaching() {
  return (
    <Section
      id="sec-teaching"
      label="Teaching · The four teammate modes"
      eyebrow="Four ways to use AI well. Three ways to use it badly. One question to ask first."
    >
      <Subhead n="1" title="The four teammate modes" />
      <p>
        Most students treat AI like a vending machine. Type, get, paste. That
        is one mode out of four, and it is the weakest one. The strong moves
        are the other three. Pick the mode before you type. Different mode,
        different prompt, different result.
      </p>
      <TeammateModes />
      <p className="mt-3 text-sm text-muted">
        Notice the pattern: every mode keeps you doing the thinking. The AI
        moves around you. You do not move around it.
      </p>

      <Subhead n="2" title="The thing you have to name" />
      <p>
        Before we talk about how to stay sharp, you need a vocabulary word for
        what happens when you do not. There is a phrase for the slow-motion
        cost of using AI badly. It is the single most important word in this
        lesson.
      </p>
      <VocabCard
        term="Cognitive offloading"
        definition="Letting a tool do thinking you could be doing yourself. Sometimes useful, sometimes corrosive. The question is whether the muscle you skipped is one you needed."
        analogy="A calculator does the arithmetic so you can think about the problem. A calculator that also tells you what problem to solve is a different kind of tool."
        hook="Offload the boring. Keep the deciding."
        matters="AI makes offloading frictionless. That is the danger and the gift. Used well, it offloads typing, reformatting, and recall so you can think harder. Used badly, it offloads the thinking itself and the muscle goes away."
      />

      <Subhead n="3" title="The three traps" />
      <p>
        Three things you should not hand to AI, even when you can. These are
        not rules. They are warnings. Every one of them has cost real people
        real ground.
      </p>
      <Traps />
      <p className="mt-3 text-sm text-muted">
        Pattern: anything that makes you, you. Your taste, your voice, your
        judgment. AI can help with all three. AI cannot do any of the three
        for you without you slowly disappearing.
      </p>

      <Subhead n="4" title="The 60-second move" />
      <p>
        One habit that keeps you in the seat. Before you open AI for anything
        that takes more than a paragraph: spend 60 seconds writing what you
        already think. A rough thesis. A first guess. A starting frame. Then
        ask AI. Then compare.
      </p>
      <SixtySecondMove />
      <p className="mt-3 text-sm text-muted">
        The gap between your first draft and the AI answer is where learning
        actually happens. Skip the 60 seconds and you skip the learning.
      </p>
    </Section>
  );
}

// ---------- Your turn ----------

function YourTurn() {
  return (
    <Section
      id="sec-your-turn"
      label="Your turn · Pick the mode"
      eyebrow="Three scenarios. Pick the teammate mode. Or walk away."
    >
      <p>
        For each scenario, choose the mode that fits. Some questions have a
        clear best mode. Some are walk-away. Read each one carefully, pick,
        then check the verdict.
      </p>

      <PostureDrill
        n={1}
        scenario="You have to write a 5-page essay on whether the New Deal was a success. You have read the chapter. You have opinions but they are mushy. The essay is due Friday."
        correct="sparring"
        verdict="Sparring partner. Write a rough thesis in two sentences. Ask the AI to push back: what is the strongest counter-argument, what evidence would change my mind, where am I being lazy. You sharpen, then write. The essay is yours. The AI made you defend it."
      />

      <PostureDrill
        n={2}
        scenario="Your calculus teacher introduced related rates today. You followed the first example. You are lost on the second. The homework is six problems."
        correct="tutor"
        verdict="Tutor. Ask the AI to explain related rates two different ways, with a worked example you did not see in class, then ask it to walk you through one of your homework problems step by step before you try the rest yourself. Do not paste the answers. You are here to learn the move, not to get past tonight."
      />

      <PostureDrill
        n={3}
        scenario="You are applying to four colleges. Each one has a 'Why us' essay. You are exhausted and you have a draft of one. You think it is okay."
        correct="editor"
        verdict="Editor. Hand it your draft and ask: where does this feel generic, what specific detail is missing, which sentence sounds like every other applicant. Do not let it rewrite the essay. Use the notes. Rewrite it yourself. The admissions reader can smell AI voice from across the room. They can also smell yours, in a good way."
      />

      <PostureDrill
        n={4}
        scenario="You and your best friend are in a fight. You want to text them but you are not sure what to say. You think about asking AI to write the text."
        correct="walk-away"
        verdict="Walk away. The AI does not know your friend, your history, or your tone. Anything it writes will sound like a stranger trying to fix your relationship. Write it badly yourself. Or call. Or wait a day. This one is not the AI's question to answer."
      />
    </Section>
  );
}

// ---------- Quick check ----------

function QuickCheck() {
  const items = [
    {
      q: 'You have a rough thesis and the assignment is due tomorrow. Which teammate mode fits best?',
      a: 'Sparring partner. Ask the AI to push back on your thesis, find the weakest evidence, and surface the counter-argument before you draft. The essay stays yours, but the thinking gets harder.',
    },
    {
      q: 'Define cognitive offloading in one sentence.',
      a: 'Letting a tool do thinking you could be doing yourself, which is sometimes useful and sometimes corrosive depending on whether the muscle you skipped was one you needed.',
    },
    {
      q: 'Name the three traps from this lesson.',
      a: 'Outsourcing your taste (letting AI decide what is good). Outsourcing your voice (letting AI write the way you sound). Outsourcing your judgment (letting AI make the call).',
    },
    {
      q: 'What is the 60-second move and what does it protect?',
      a: 'Before asking AI anything that matters, spend 60 seconds writing what you already think. It protects the learning that happens in the gap between your first draft and the AI answer.',
    },
    {
      q: 'Same essay assignment, same B+ grade. What makes Student B different from Student A six months later?',
      a: 'Student B used AI to push their own thinking, so the muscle that writes essays got stronger. Student A used AI to skip the thinking, so the muscle got weaker. The grade is identical. The person is not.',
    },
  ];

  return (
    <Section
      id="sec-quick-check"
      label="Quick check"
      eyebrow="Five questions. Answer first. Then reveal."
    >
      <div className="space-y-3">
        {items.map((it, i) => (
          <QuickCheckItem key={i} n={i + 1} q={it.q} a={it.a} />
        ))}
      </div>
    </Section>
  );
}

// ---------- Lock card ----------

function LockCard() {
  return (
    <Section
      id="sec-lock-card"
      label="Lock card"
      eyebrow="Screenshot this. Look at it before every long AI session."
    >
      <div className="rounded-xl border border-amber/40 bg-gradient-to-br from-amber/[0.05] to-amber/[0.02] p-8" data-lockcard-body>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
            Lesson 2.4 · Lock card · Teammate posture
          </p>
          <button className="text-xs text-muted hover:text-text">
            ⤓ Save image
          </button>
        </div>

        <p className="mt-4 text-lg leading-relaxed text-text">
          AI works for you. It does not work instead of you. Four modes. Pick
          one before you type.
        </p>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <ModeTile
            label="Sparring partner"
            q="Push back on my idea."
            sub="You have a take. Ask AI to attack it. You sharpen, then write."
          />
          <ModeTile
            label="Drafter"
            q="Give me a starting point."
            sub="You are stuck on the blank page. Get a rough draft to react to. Then rewrite it in your voice."
          />
          <ModeTile
            label="Tutor"
            q="Explain this until I get it."
            sub="You are missing a concept. Ask for two explanations and a worked example. Then try one yourself."
          />
          <ModeTile
            label="Editor"
            q="Find what is broken."
            sub="You wrote it. Ask AI to find the weakest paragraph, the generic line, the missing detail. You decide what to fix."
          />
        </div>

        <div className="mt-6 rounded-lg border border-line bg-ink/60 p-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            The principle behind the modes
          </p>
          <p className="mt-2 text-sm leading-relaxed text-text/90">
            Cognitive offloading is fine for the boring parts: typing,
            reformatting, recall. It is corrosive for the parts that make you,
            you: taste, voice, judgment. The four modes keep your hands on
            those three. Use AI to think harder, not less.
          </p>
        </div>

        <p className="mt-6 text-sm italic text-muted">
          Spar. Draft. Tutor. Edit. Never replace. Always in the seat.
        </p>
      </div>
    </Section>
  );
}

// ---------- Custom components for 2.4 ----------

function StudentSplit() {
  return (
    <div className="my-6 grid gap-4 lg:grid-cols-2">
      <StudentCard
        variant="bad"
        label="Student A"
        sub="Wednesday night. 47 minutes."
        steps={[
          'Opens AI. Pastes the assignment prompt.',
          'Reads the response once. Looks fine.',
          'Copies it into the doc. Changes three words so it sounds like them.',
          'Submits Thursday morning. Gets a B+.',
        ]}
        verdict="What got stronger this week: nothing. The essay exists. The student is exactly where they started, minus 47 minutes."
      />
      <StudentCard
        variant="good"
        label="Student B"
        sub="Wednesday night. Two hours."
        steps={[
          'Writes a rough thesis themselves: “The New Deal worked, but unevenly.” Two paragraphs of mush.',
          'Asks AI: “Push back on this thesis. What is the strongest counter-argument?”',
          'Rewrites the thesis. Now it has an opponent.',
          'Drafts the essay. Asks AI: “Which paragraph is the weakest, and why?” Rewrites that one.',
          'Submits Thursday morning. Gets a B+.',
        ]}
        verdict="What got stronger this week: their thesis muscle, their counter-argument instinct, their self-editing eye. The essay exists, and so does a sharper writer."
      />
    </div>
  );
}

function StudentCard({
  variant,
  label,
  sub,
  steps,
  verdict,
}: {
  variant: 'good' | 'bad';
  label: string;
  sub: string;
  steps: string[];
  verdict: string;
}) {
  const styles =
    variant === 'good'
      ? {
          border: 'border-emerald-500/30',
          bg: 'bg-emerald-500/[0.04]',
          badgeBg: 'bg-emerald-500/20',
          badgeText: 'text-emerald-300',
          numText: 'text-emerald-300',
          verdictBorder: 'border-emerald-500/30',
          verdictBg: 'bg-emerald-500/[0.05]',
        }
      : {
          border: 'border-rose-500/30',
          bg: 'bg-rose-500/[0.04]',
          badgeBg: 'bg-rose-500/20',
          badgeText: 'text-rose-300',
          numText: 'text-rose-300',
          verdictBorder: 'border-rose-500/30',
          verdictBg: 'bg-rose-500/[0.05]',
        };
  return (
    <div className={`rounded-xl border ${styles.border} ${styles.bg} p-5`}>
      <div className="flex items-baseline justify-between gap-3">
        <span
          className={`inline-block w-fit shrink-0 rounded-md ${styles.badgeBg} px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${styles.badgeText}`}
        >
          {label}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
          {sub}
        </span>
      </div>
      <ol className="mt-4 space-y-2.5">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm text-text/90">
            <span
              className={`mt-0.5 font-mono text-[10px] uppercase tracking-widest ${styles.numText}`}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="flex-1 leading-relaxed">{s}</span>
          </li>
        ))}
      </ol>
      <div
        className={`mt-4 rounded-md border ${styles.verdictBorder} ${styles.verdictBg} px-4 py-3`}
      >
        <p
          className={`font-mono text-[10px] uppercase tracking-widest ${styles.badgeText}`}
        >
          Six months later
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-text/90">{verdict}</p>
      </div>
    </div>
  );
}

function TeammateModes() {
  const modes = [
    {
      label: 'Sparring partner',
      when: 'You already have a take. Thesis, idea, opinion, plan.',
      how: '"Push back on this. What is the strongest counter-argument? Where am I being lazy or wrong?"',
      move: 'You sharpen the take. AI is a smarter opponent than your own brain.',
    },
    {
      label: 'Drafter',
      when: 'Blank page. You know what you want but cannot start.',
      how: '"Give me a rough draft of X in Y voice. I will rewrite it."',
      move: 'You react to the draft. Cut, replace, rewrite in your voice. Never submit the draft.',
    },
    {
      label: 'Tutor',
      when: 'You do not understand a concept. The textbook lost you.',
      how: '"Explain X two different ways. Give me one worked example I have not seen. Then walk me through one problem step by step."',
      move: 'You learn the move, then try the next one alone. Tutor goes away. Skill stays.',
    },
    {
      label: 'Editor',
      when: 'You have a draft. You wrote it. It is not done.',
      how: '"Find the weakest paragraph. What is generic? What sentence sounds like every other student?"',
      move: 'You decide what to keep. You do the rewrites. AI points; you cut.',
    },
  ];
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="grid grid-cols-[140px_minmax(0,1fr)] border-b border-line bg-elevated/40 px-5 py-2.5 text-[10px] font-mono uppercase tracking-widest text-muted">
        <span>Mode</span>
        <span>When · How · Move</span>
      </div>
      {modes.map((m, i) => (
        <div
          key={m.label}
          className={`grid grid-cols-[140px_minmax(0,1fr)] gap-4 px-5 py-4 ${
            i < modes.length - 1 ? 'border-b border-line' : ''
          }`}
        >
          <div>
            <p className="font-semibold text-text">{m.label}</p>
          </div>
          <div className="space-y-2 text-sm text-text/90">
            <p>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                When ·{' '}
              </span>
              {m.when}
            </p>
            <p>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                How ·{' '}
              </span>
              <span className="italic text-text/80">{m.how}</span>
            </p>
            <p>
              <span className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Move ·{' '}
              </span>
              {m.move}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function Traps() {
  const traps = [
    {
      label: 'Outsourcing your taste',
      looks:
        'You let AI tell you which essay opener is best, which song to listen to, which idea is good. You stop noticing what you like.',
      instead:
        'Pick first. Ask AI to pressure-test your pick. Your taste is built by choosing under uncertainty, not by getting handed an answer.',
    },
    {
      label: 'Outsourcing your voice',
      looks:
        'You let AI write the way you sound. Polished, generic, smooth. The reader hears no one. Including you, when you read it back.',
      instead:
        'Write first. Use AI as editor on your own words. The bumps in your voice are the voice. Polish kills it.',
    },
    {
      label: 'Outsourcing your judgment',
      looks:
        'You hand over decisions: what to apply to, who to text, what to believe. The AI cannot be wrong, because you stopped having a position.',
      instead:
        'Decide first. Use AI to surface what you missed. The decision stays yours, with all the weight that comes with it.',
    },
  ];
  return (
    <div className="my-6 space-y-3">
      {traps.map((t, i) => (
        <div
          key={t.label}
          className="rounded-lg border border-rose-500/25 bg-rose-500/[0.03] p-5"
        >
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-[11px] uppercase tracking-widest text-rose-300">
              Trap {String(i + 1).padStart(2, '0')}
            </span>
            <p className="font-semibold text-text">{t.label}</p>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-line bg-ink/40 px-4 py-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
                What it looks like
              </p>
              <p className="mt-1.5 text-sm text-text/90">{t.looks}</p>
            </div>
            <div className="rounded-md border border-amber/30 bg-amber/[0.04] px-4 py-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Do this instead
              </p>
              <p className="mt-1.5 text-sm text-text/90">{t.instead}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SixtySecondMove() {
  const steps = [
    {
      label: 'Before',
      sub: '60 seconds',
      body: 'Write what you already think. Rough thesis, first guess, your best frame. Bad sentences welcome.',
    },
    {
      label: 'Ask',
      sub: 'AI in mode',
      body: 'Pick your mode (spar, draft, tutor, edit) and ask. Paste your 60 seconds into the prompt if it helps.',
    },
    {
      label: 'Compare',
      sub: 'The gap',
      body: 'Where does the AI agree with you? Where does it push back? The gap is where you learn something or where you decide AI is wrong.',
    },
    {
      label: 'Decide',
      sub: 'You',
      body: 'You write the final version. Some AI ideas in, some out. You can defend every line because you saw both sides.',
    },
  ];
  return (
    <div className="my-6 grid gap-3 sm:grid-cols-2">
      {steps.map((s, i) => (
        <div
          key={s.label}
          className="rounded-lg border border-line bg-surface p-4"
        >
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
              {s.sub}
            </span>
          </div>
          <p className="mt-2 font-semibold text-text">{s.label}</p>
          <p className="mt-1.5 text-sm leading-relaxed text-text/90">{s.body}</p>
        </div>
      ))}
    </div>
  );
}

type PostureMode = 'sparring' | 'drafter' | 'tutor' | 'editor' | 'walk-away';

function PostureDrill({
  n,
  scenario,
  correct,
  verdict,
}: {
  n: number;
  scenario: string;
  correct: PostureMode;
  verdict: string;
}) {
  const [picked, setPicked] = useState<PostureMode | null>(null);
  const isCorrect = picked === correct;
  const modes: { id: PostureMode; label: string; sub: string }[] = [
    { id: 'sparring', label: 'Sparring partner', sub: 'Push back on my take.' },
    { id: 'drafter', label: 'Drafter', sub: 'Give me a rough start.' },
    { id: 'tutor', label: 'Tutor', sub: 'Explain until I get it.' },
    { id: 'editor', label: 'Editor', sub: 'Find what is broken.' },
    { id: 'walk-away', label: 'Walk away', sub: 'Not the AI\'s question.' },
  ];
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Drill {n} · The scenario
        </p>
        <p className="mt-1.5 text-sm text-text/90">{scenario}</p>
      </div>
      <div className="px-5 py-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Pick the mode
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {modes.map((m) => {
            const selected = picked === m.id;
            const verdictState: 'right' | 'wrong' | null = picked
              ? correct === m.id
                ? 'right'
                : selected
                ? 'wrong'
                : null
              : null;
            return (
              <ModeButton
                key={m.id}
                label={m.label}
                sub={m.sub}
                selected={selected}
                verdict={verdictState}
                onClick={() => setPicked(m.id)}
              />
            );
          })}
        </div>

        {picked && (
          <div
            className={`mt-5 rounded-md border px-4 py-3 ${
              isCorrect
                ? 'border-emerald-500/30 bg-emerald-500/[0.04]'
                : 'border-amber/40 bg-amber/[0.04]'
            }`}
          >
            <p
              className={`font-mono text-[10px] uppercase tracking-widest ${
                isCorrect ? 'text-emerald-300' : 'text-amber'
              }`}
            >
              {isCorrect ? 'Correct' : 'Not quite'} · Model verdict
            </p>
            <p className="mt-1.5 text-sm text-text/90">{verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ModeButton({
  label,
  sub,
  selected,
  verdict,
  onClick,
}: {
  label: string;
  sub: string;
  selected: boolean;
  verdict: 'right' | 'wrong' | null;
  onClick: () => void;
}) {
  const borderClass =
    verdict === 'right' && selected
      ? 'border-emerald-500/50 bg-emerald-500/[0.05]'
      : verdict === 'right' && !selected
      ? 'border-emerald-500/30 bg-emerald-500/[0.03]'
      : verdict === 'wrong' && selected
      ? 'border-rose-500/50 bg-rose-500/[0.05]'
      : selected
      ? 'border-amber bg-amber/[0.06]'
      : 'border-line bg-ink/40 hover:border-amber/50';
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-lg border p-3 transition-colors ${borderClass}`}
    >
      <p
        className={`font-mono text-[10px] uppercase tracking-widest ${
          selected ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <p className="mt-1.5 text-xs text-text/90">{sub}</p>
    </button>
  );
}

function ModeTile({
  label,
  q,
  sub,
}: {
  label: string;
  q: string;
  sub: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-ink/60 p-5">
      <span className="inline-block w-fit shrink-0 rounded-md bg-amber/20 px-2 py-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
        {label}
      </span>
      <p className="mt-3 text-base font-semibold text-text">{q}</p>
      <p className="mt-2 text-sm leading-relaxed text-text/90">{sub}</p>
    </div>
  );
}

// ---------- Next up ----------

function NextUp() {
  return (
    <div className="mt-12 rounded-lg border border-line bg-surface p-6" data-nextup-card>
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
        Up next · Lesson 2.5
      </p>
      <div className="mt-2 flex items-start justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-text">
            Week 2 capstone: A full AI workflow, end to end
          </h3>
          <p className="mt-2 text-sm text-muted">
            One real task. Pick the tool, write the prompt, audit the answer,
            zone it, posture yourself. Everything from 2.1 through 2.4 in one
            sitting. The week ends with you running the whole loop without
            looking back at the lessons.
          </p>
        </div>
        <button className="shrink-0 rounded-md border border-line px-4 py-2 text-sm text-muted hover:border-amber hover:text-text">
          Coming soon
        </button>
      </div>
    </div>
  );
}

// ---------- Shared primitives ----------

function Subhead({ n, title }: { n: string; title: string }) {
  return (
    <h3 className="mt-8 flex items-baseline gap-3 text-lg font-semibold text-text">
      <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
        {n}
      </span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  hook,
  matters,
}: {
  term: string;
  definition: string;
  analogy: string;
  hook: string;
  matters: string;
}) {
  const [draft, setDraft] = useState('');
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Vocab · 2.4 · New term
        </p>
        <h4 className="mt-1 text-xl font-semibold text-text">{term}</h4>
      </div>
      <div className="space-y-4 px-5 py-5">
        {!revealed && (
          <>
            <p className="text-sm text-muted">
              Write your own one-sentence definition first. Then reveal.
            </p>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Your definition of "${term}"...`}
              className="h-20 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/60 focus:border-amber focus:outline-none"
            />
            <button
              onClick={() => setRevealed(true)}
              className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
            >
              Reveal definition →
            </button>
          </>
        )}
        {revealed && (
          <div className="space-y-3">
            <Block label="Definition" body={definition} />
            <Block label="Analogy" body={analogy} accent="amber" />
            <Block label="Memory hook" body={hook} />
            <Block label="Why it matters" body={matters} />
          </div>
        )}
      </div>
    </div>
  );
}

function Block({
  label,
  body,
  accent = 'plain',
}: {
  label: string;
  body: string;
  accent?: 'plain' | 'amber';
}) {
  return (
    <div
      className={`rounded-md border px-4 py-3 ${
        accent === 'amber'
          ? 'border-amber/40 bg-amber/[0.04]'
          : 'border-line bg-ink/40'
      }`}
    >
      <p
        className={`font-mono text-[10px] uppercase tracking-widest ${
          accent === 'amber' ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, a }: { n: number; q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-start gap-3 p-5">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          Q{n}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          {open && (
            <div className="mt-3 rounded-md border border-amber/30 bg-amber/[0.04] p-3 text-sm text-text">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Model answer
              </p>
              <p className="mt-1.5">{a}</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-line px-3 py-1.5 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
    </div>
  );
}

function Section({
  id,
  label,
  eyebrow,
  accent = 'plain',
  children,
}: {
  id: string;
  label: string;
  eyebrow: string;
  accent?: 'amber' | 'blue' | 'rose' | 'plain';
  children: React.ReactNode;
}) {
  const colorMap = {
    amber: 'text-amber',
    blue: 'text-[#7DC8E8]',
    rose: 'text-[#E89F8B]',
    plain: 'text-text',
  };
  return (
    <section
      id={id}
      className="scroll-mt-24 border-t border-line py-12 first-of-type:border-t-0 first-of-type:pt-0"
    >
      <div className="mb-6">
        <p
          className={`font-mono text-[11px] uppercase tracking-[0.18em] ${colorMap[accent]}`}
        >
          {label}
        </p>
        <p className="mt-1.5 text-sm text-muted">{eyebrow}</p>
      </div>
      <div className="space-y-4 text-base leading-relaxed text-text/90">
        {children}
      </div>
    </section>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    { id: 'sec-cold-open', label: 'Cold open' },
    { id: 'sec-from-last-time', label: '2.3 review' },
    { id: 'sec-teaching', label: 'Teammate modes' },
    { id: 'sec-your-turn', label: 'Your turn' },
    { id: 'sec-quick-check', label: 'Quick check' },
    { id: 'sec-lock-card', label: 'Lock card' },
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ul className="mt-4 space-y-1">
        {items.map((it, i) => {
          const active = i === activeIndex;
          const done = i < activeIndex;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className={`flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:bg-elevated/60 hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border font-mono text-[10px] ${
                    active
                      ? 'border-amber bg-amber text-ink'
                      : done
                      ? 'border-amber/40 text-amber'
                      : 'border-line text-muted'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                {it.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
